import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Typography,
  Descriptions,
  Progress,
  Spin,
  Button,
  Space,
  Tooltip,
  Divider,
  Empty,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  UserOutlined,
  BellOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  ReloadOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchPushStats,
  selectPushStats,
  selectStatsLoading,
  selectStatsError,
  clearError,
} from 'store/slices/stats.slice';
import { formatters } from 'utils/formatters';
import { notify } from 'utils/helpers';

const { Title, Text, Paragraph } = Typography;

const PushDetail: React.FC = () => {
  const { messageId } = useParams<{ messageId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const pushStats = useAppSelector(selectPushStats);
  const isLoading = useAppSelector(selectStatsLoading);
  const error = useAppSelector(selectStatsError);

  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    if (messageId) {
      dispatch(fetchPushStats(messageId));
    }

    return () => {
      dispatch(clearError());
    };
  }, [messageId, dispatch]);
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (autoRefresh && messageId && pushStats?.push_info?.status === 'sending') {
      interval = setInterval(() => {
        dispatch(fetchPushStats(messageId));
      }, 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, messageId, pushStats?.push_info?.status, dispatch]);
  useEffect(() => {
    if (pushStats?.push_info?.status === 'sending') {
      setAutoRefresh(true);
    } else {
      setAutoRefresh(false);
    }
  }, [pushStats?.push_info?.status]);

  const handleRefresh = () => {
    if (messageId) {
      dispatch(fetchPushStats(messageId));
    }
  };

  const handleCopyMessageId = () => {
    if (messageId) {
      navigator.clipboard.writeText(messageId);
      notify.success('Message ID скопирован');
    }
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { color: string; icon: React.ReactNode; label: string; description: string }> = {
      scheduled: {
        color: 'blue',
        icon: <ClockCircleOutlined />,
        label: 'Запланировано',
        description: 'Пуш ожидает отправки по расписанию',
      },
      sending: {
        color: 'processing',
        icon: <SendOutlined />,
        label: 'Отправляется',
        description: 'Пуш находится в процессе отправки пользователям',
      },
      sent: {
        color: 'success',
        icon: <CheckCircleOutlined />,
        label: 'Отправлено',
        description: 'Пуш успешно отправлен всем целевым пользователям',
      },
      failed: {
        color: 'error',
        icon: <CloseCircleOutlined />,
        label: 'Ошибка',
        description: 'При отправке произошла ошибка',
      },
      cancelled: {
        color: 'default',
        icon: <StopOutlined />,
        label: 'Отменено',
        description: 'Отправка была отменена',
      },
    };

    return configs[status] || configs.scheduled;
  };

  const getPushTypeConfig = (type: string) => {
    const configs: Record<string, { color: string; label: string }> = {
      regular: { color: 'blue', label: 'Обычный' },
      event: { color: 'purple', label: 'Событийный' },
      special: { color: 'gold', label: 'Специальный' },
    };

    return configs[type] || { color: 'default', label: type };
  };

  const getConfigTypeLabel = (type: string) => {
    return type === 'campaign' ? 'Кампания' : 'Одиночный';
  };

  const getProgressColor = (percent: number, thresholds = { good: 80, warn: 50 }) => {
    if (percent >= thresholds.good) return '#52c41a';
    if (percent >= thresholds.warn) return '#faad14';
    return '#ff4d4f';
  };

  if (isLoading && !pushStats) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Загрузка данных пуша..." />
      </div>
    );
  }

  if (error && !pushStats) {
    return (
      <div style={{ padding: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ marginBottom: 16 }}
        >
          Назад
        </Button>
        <Alert
          type="error"
          showIcon
          message="Ошибка загрузки"
          description={error}
          action={
            <Button size="small" onClick={handleRefresh}>
              Повторить
            </Button>
          }
        />
      </div>
    );
  }

  if (!pushStats) {
    return (
      <div style={{ padding: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ marginBottom: 16 }}
        >
          Назад
        </Button>
        <Empty description="Данные не найдены" />
      </div>
    );
  }

  const { statistics, push_info, metrics } = pushStats;
  const statusConfig = push_info ? getStatusConfig(push_info.status) : null;
  const pushTypeConfig = push_info ? getPushTypeConfig(push_info.type) : null;

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
            >
              Назад
            </Button>
            <Divider type="vertical" />
            <BellOutlined style={{ fontSize: 20, color: '#1890ff' }} />
            <Title level={4} style={{ margin: 0 }}>
              Детализация пуша
            </Title>
          </Space>

          <Space>
            {autoRefresh && (
              <Tag color="processing" icon={<ReloadOutlined spin />}>
                Автообновление
              </Tag>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={isLoading}
            >
              Обновить
            </Button>
          </Space>
        </Space>
      </div>
      {push_info && statusConfig && (
        <Alert
          type={
            push_info.status === 'sent'
              ? 'success'
              : push_info.status === 'failed'
              ? 'error'
              : push_info.status === 'sending'
              ? 'info'
              : 'warning'
          }
          showIcon
          icon={statusConfig.icon}
          message={
            <Space>
              <Tag color={statusConfig.color} style={{ margin: 0 }}>
                {statusConfig.label}
              </Tag>
              <Text type="secondary">{statusConfig.description}</Text>
            </Space>
          }
          style={{ marginBottom: 24 }}
        />
      )}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small" hoverable>
            <Statistic
              title="Отправлено"
              value={statistics.sent_count}
              prefix={<SendOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" hoverable>
            <Statistic
              title="Доставлено"
              value={statistics.delivered_count}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" hoverable>
            <Statistic
              title="Кликов"
              value={statistics.clicked_count}
              prefix={<ThunderboltOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" hoverable>
            <Statistic
              title="Ошибки"
              value={statistics.failed_count}
              prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: statistics.failed_count > 0 ? '#ff4d4f' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BarChartOutlined />
                <span>Эффективность</span>
              </Space>
            }
            size="small"
          >
            <Row gutter={[24, 24]}>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Progress
                    type="dashboard"
                    percent={Number(metrics.delivery_rate?.toFixed(1)) || 0}
                    size={100}
                    strokeColor={getProgressColor(metrics.delivery_rate || 0)}
                    format={(percent) => (
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 600 }}>{percent}%</div>
                        <div style={{ fontSize: 11, color: '#999' }}>Доставка</div>
                      </div>
                    )}
                  />
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Progress
                    type="dashboard"
                    percent={Number(metrics.ctr?.toFixed(1)) || 0}
                    size={100}
                    strokeColor={getProgressColor(metrics.ctr || 0, { good: 10, warn: 5 })}
                    format={(percent) => (
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 600 }}>{percent}%</div>
                        <div style={{ fontSize: 11, color: '#999' }}>CTR</div>
                      </div>
                    )}
                  />
                </div>
              </Col>
              <Col span={8}>
                <div style={{ textAlign: 'center' }}>
                  <Progress
                    type="dashboard"
                    percent={Number(metrics.failure_rate?.toFixed(1)) || 0}
                    size={100}
                    strokeColor={
                      (metrics.failure_rate || 0) > 10
                        ? '#ff4d4f'
                        : (metrics.failure_rate || 0) > 5
                        ? '#faad14'
                        : '#52c41a'
                    }
                    format={(percent) => (
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 600 }}>{percent}%</div>
                        <div style={{ fontSize: 11, color: '#999' }}>Ошибки</div>
                      </div>
                    )}
                  />
                </div>
              </Col>
            </Row>
            <Divider style={{ margin: '16px 0' }} />
            <div>
              <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                Воронка доставки
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Tag color="blue">
                  Отправлено: {formatters.number(statistics.sent_count)}
                </Tag>
                <span style={{ color: '#ccc' }}>→</span>
                <Tag color="green">
                  Доставлено: {formatters.number(statistics.delivered_count)}
                </Tag>
                <span style={{ color: '#ccc' }}>→</span>
                <Tag color="purple">
                  Клики: {formatters.number(statistics.clicked_count)}
                </Tag>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <UserOutlined />
                <span>Атрибуция конверсий</span>
              </Space>
            }
            size="small"
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Регистрации"
                  value={statistics.attributed_registrations}
                  suffix={
                    statistics.sent_count > 0 && (
                      <Text type="secondary" style={{ fontSize: 14 }}>
                        ({((statistics.attributed_registrations / statistics.sent_count) * 100).toFixed(2)}%)
                      </Text>
                    )
                  }
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Депозиты"
                  value={statistics.attributed_deposits}
                  suffix={
                    statistics.sent_count > 0 && (
                      <Text type="secondary" style={{ fontSize: 14 }}>
                        ({((statistics.attributed_deposits / statistics.sent_count) * 100).toFixed(2)}%)
                      </Text>
                    )
                  }
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
            </Row>

            <Divider style={{ margin: '16px 0' }} />

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Конверсия в регистрацию (от кликов)
                  </Text>
                  <Progress
                    percent={
                      statistics.clicked_count > 0
                        ? Number(((statistics.attributed_registrations / statistics.clicked_count) * 100).toFixed(1))
                        : 0
                    }
                    strokeColor="#52c41a"
                    size="small"
                    style={{ marginTop: 4 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Конверсия в депозит (от кликов)
                  </Text>
                  <Progress
                    percent={
                      statistics.clicked_count > 0
                        ? Number(((statistics.attributed_deposits / statistics.clicked_count) * 100).toFixed(1))
                        : 0
                    }
                    strokeColor="#faad14"
                    size="small"
                    style={{ marginTop: 4 }}
                  />
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
      {push_info && (
        <Card
          title={
            <Space>
              <BellOutlined />
              <span>Информация о пуше</span>
            </Space>
          }
          size="small"
          style={{ marginBottom: 24 }}
        >
          <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} bordered size="small">
            <Descriptions.Item label="Message ID">
              <Space>
                <Text code style={{ fontSize: 12 }}>
                  {messageId}
                </Text>
                <Tooltip title="Скопировать">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={handleCopyMessageId}
                  />
                </Tooltip>
              </Space>
            </Descriptions.Item>

            <Descriptions.Item label="Тип пуша">
              {pushTypeConfig && (
                <Tag color={pushTypeConfig.color}>{pushTypeConfig.label}</Tag>
              )}
            </Descriptions.Item>

            <Descriptions.Item label="Конфигурация">
              <Tag>{getConfigTypeLabel(push_info.config_type)}</Tag>
            </Descriptions.Item>

            <Descriptions.Item label="Заголовок" span={3}>
              <Text strong>{push_info.title}</Text>
            </Descriptions.Item>

            <Descriptions.Item label="Текст" span={3}>
              <Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {push_info.body}
              </Paragraph>
            </Descriptions.Item>

            {push_info.scheduled_at && (
              <Descriptions.Item label="Запланировано на">
                <ClockCircleOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                {formatters.dateTime(push_info.scheduled_at)}
              </Descriptions.Item>
            )}

            {push_info.sent_at && (
              <Descriptions.Item label="Отправлено в">
                <SendOutlined style={{ marginRight: 4, color: '#52c41a' }} />
                {formatters.dateTime(push_info.sent_at)}
              </Descriptions.Item>
            )}

            <Descriptions.Item label="Статус">
              {statusConfig && (
                <Tag icon={statusConfig.icon} color={statusConfig.color}>
                  {statusConfig.label}
                </Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
      <Card
        title={
          <Space>
            <BarChartOutlined />
            <span>Сводка статистики</span>
          </Space>
        }
        size="small"
      >
        <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} bordered size="small">
          <Descriptions.Item label="Всего отправлено">
            {formatters.number(statistics.sent_count)}
          </Descriptions.Item>
          <Descriptions.Item label="Доставлено">
            {formatters.number(statistics.delivered_count)}
            {statistics.sent_count > 0 && (
              <Text type="secondary"> ({((statistics.delivered_count / statistics.sent_count) * 100).toFixed(1)}%)</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Кликов">
            {formatters.number(statistics.clicked_count)}
            {statistics.sent_count > 0 && (
              <Text type="secondary"> ({((statistics.clicked_count / statistics.sent_count) * 100).toFixed(1)}%)</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Ошибок">
            <Text type={statistics.failed_count > 0 ? 'danger' : undefined}>
              {formatters.number(statistics.failed_count)}
            </Text>
            {statistics.sent_count > 0 && statistics.failed_count > 0 && (
              <Text type="danger"> ({((statistics.failed_count / statistics.sent_count) * 100).toFixed(1)}%)</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Атрибуция: Регистрации">
            {statistics.attributed_registrations}
          </Descriptions.Item>
          <Descriptions.Item label="Атрибуция: Депозиты">
            {statistics.attributed_deposits}
          </Descriptions.Item>
          <Descriptions.Item label="Последнее обновление" span={3}>
            {formatters.dateTime(statistics.updated_at)}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default PushDetail;